<template>
    <Head title="Rental History Verification" />

    <GuestLayout>
        <div class="min-h-screen bg-white py-8 px-4 sm:px-6">
            <div class="mx-auto max-w-lg">
                <div class="mb-8">
                    <JetAuthenticationCardLogoColored class="h-10 w-auto" />
                </div>

                <template v-if="alreadySubmitted">
                    <h1 class="text-2xl font-bold text-gray-900">Thank you</h1>
                    <p class="mt-3 text-sm text-gray-600">
                        Your responses for {{ verification.applicant_name }} have already been submitted.
                    </p>
                </template>

                <template v-else>
                    <h1 class="text-2xl font-bold text-gray-900">Rental history verification</h1>
                    <p class="mt-3 text-sm text-gray-600">
                        You were listed as a reference and authorized by the applicant to verify their rental history.
                        Please review the information provided — it should only take a minute.
                    </p>

                    <div class="mt-6 rounded-lg bg-gray-100 px-4 py-3">
                        <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Applicant</p>
                        <p class="mt-1 text-lg font-semibold text-gray-900">{{ verification.applicant_name }}</p>
                    </div>

                    <form class="mt-8 space-y-8" @submit.prevent="submit">
                        <section>
                            <h2 class="text-lg font-semibold text-gray-900">Rental details</h2>
                            <div class="mt-4 space-y-3 text-sm text-gray-700">
                                <div v-if="verification.rental_address">
                                    <p class="font-medium text-gray-900">Address</p>
                                    <p>{{ verification.rental_address }}</p>
                                </div>
                                <div v-if="verification.monthly_rent">
                                    <p class="font-medium text-gray-900">Monthly rent</p>
                                    <p>{{ verification.monthly_rent }}</p>
                                </div>
                                <div v-if="verification.move_in_date">
                                    <p class="font-medium text-gray-900">Move-in date</p>
                                    <p>{{ verification.move_in_date }}</p>
                                </div>
                                <p v-if="!verification.rental_address && !verification.monthly_rent && !verification.move_in_date" class="text-gray-500">No rental details were provided.</p>
                            </div>

                            <div class="mt-6">
                                <p class="text-sm font-medium text-gray-900">Is the information provided true and correct?</p>
                                <div class="mt-3 space-y-2">
                                    <YesNoOption v-model="form.info_accurate" name="info_accurate" label="Yes" :value="true" />
                                    <YesNoOption v-model="form.info_accurate" name="info_accurate" label="No" :value="false" />
                                </div>
                                <p v-if="form.errors.info_accurate" class="mt-2 text-sm text-red-500">{{ form.errors.info_accurate }}</p>
                            </div>
                        </section>

                        <section class="border-t border-gray-200 pt-8">
                            <h2 class="text-lg font-semibold text-gray-900">Tenant history</h2>

                            <div class="mt-6 space-y-6">
                                <div>
                                    <p class="text-sm font-medium text-gray-900">Did the applicant pay rent on time?</p>
                                    <div class="mt-3 space-y-2">
                                        <YesNoOption v-model="form.paid_on_time" name="paid_on_time" label="Yes" :value="true" />
                                        <YesNoOption v-model="form.paid_on_time" name="paid_on_time" label="No" :value="false" />
                                    </div>
                                    <p v-if="form.errors.paid_on_time" class="mt-2 text-sm text-red-500">{{ form.errors.paid_on_time }}</p>
                                </div>

                                <div>
                                    <p class="text-sm font-medium text-gray-900">Did they take reasonable care of the property?</p>
                                    <div class="mt-3 space-y-2">
                                        <YesNoOption v-model="form.cared_for_property" name="cared_for_property" label="Yes" :value="true" />
                                        <YesNoOption v-model="form.cared_for_property" name="cared_for_property" label="No" :value="false" />
                                    </div>
                                    <p v-if="form.errors.cared_for_property" class="mt-2 text-sm text-red-500">{{ form.errors.cared_for_property }}</p>
                                </div>

                                <div>
                                    <p class="text-sm font-medium text-gray-900">Would you rent to them again in the future?</p>
                                    <div class="mt-3 space-y-2">
                                        <YesNoOption v-model="form.would_rent_again" name="would_rent_again" label="Yes" :value="true" />
                                        <YesNoOption v-model="form.would_rent_again" name="would_rent_again" label="No" :value="false" />
                                    </div>
                                    <p v-if="form.errors.would_rent_again" class="mt-2 text-sm text-red-500">{{ form.errors.would_rent_again }}</p>
                                </div>

                                <div>
                                    <p class="text-sm font-medium text-gray-900">Any other comments or concerns about this applicant's rental history?</p>
                                    <textarea
                                        v-model="form.comments"
                                        rows="4"
                                        maxlength="150"
                                        placeholder="Comments"
                                        class="mt-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
                                    />
                                    <div class="mt-1 flex justify-between">
                                        <p v-if="form.errors.comments" class="text-sm text-red-500">{{ form.errors.comments }}</p>
                                        <span class="ml-auto text-xs text-gray-500">{{ (form.comments || '').length }}/150</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <div class="space-y-4">
                            <button
                                type="submit"
                                class="w-full rounded-md bg-rent-risk-blue-400 px-4 py-3 text-sm font-semibold text-white hover:bg-rent-risk-blue-500 focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2 disabled:opacity-50"
                                :disabled="form.processing"
                            >
                                {{ form.processing ? 'Submitting...' : 'Submit' }}
                            </button>

                            <div class="text-center">
                                <Link
                                    :href="route('reference-verification.authorization', verification.token)"
                                    class="text-sm text-rent-risk-blue-400 underline hover:text-rent-risk-blue-500"
                                >
                                    View authorization form
                                </Link>
                            </div>
                        </div>
                    </form>
                </template>
            </div>
        </div>
    </GuestLayout>
</template>

<script>
import GuestLayout from '@/Layouts/V2/GuestLayout.vue';
import JetAuthenticationCardLogoColored from '@/Jetstream/AuthenticationCardLogoColored.vue';
import YesNoOption from './Partials/YesNoOption.vue';
import { Head, Link, useForm } from '@inertiajs/vue3';

export default {
    components: {
        GuestLayout,
        JetAuthenticationCardLogoColored,
        YesNoOption,
        Head,
        Link,
    },
    props: {
        verification: { type: Object, required: true },
        alreadySubmitted: { type: Boolean, default: false },
    },
    setup(props) {
        const form = useForm({
            info_accurate: props.verification.info_accurate,
            paid_on_time: props.verification.paid_on_time,
            cared_for_property: props.verification.cared_for_property,
            would_rent_again: props.verification.would_rent_again,
            comments: props.verification.comments || '',
        });

        return { form };
    },
    methods: {
        submit() {
            this.form.post(this.route('reference-verification.store', this.verification.token));
        },
    },
};
</script>
